/**
 * useCfbLandingBoard — React view of the #728 per-user CFB landing board.
 *
 * The Settings row is the only reactive reader; the dashboard's two entry
 * points call `readCfbLandingBoard` directly, because they need the answer at
 * a click or a mount initializer rather than through a subscription.
 *
 * Shaped after `useAlertPreferences` / `useSoundSettings`: the data layer in
 * `lib/` owns storage, this hook owns cross-tab sync. The one addition is that
 * the stored value is per user, so the snapshot is re-seeded whenever the
 * signed-in account changes — without it, signing in as a second trader on a
 * shared machine would leave this row showing the first trader's choice.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  cfbLandingStorageKey,
  readCfbLandingBoard,
  writeCfbLandingBoard,
  type CfbLandingBoard,
} from '@/lib/routing/cfbLandingBoard';
import { useAuthStore } from '@/stores/authStore';

export function useCfbLandingBoard(): {
  board: CfbLandingBoard;
  setBoard: (board: CfbLandingBoard) => void;
} {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [board, setLocalBoard] = useState<CfbLandingBoard>(() => readCfbLandingBoard(userId));

  // Re-seed on an account change, DURING render rather than in an effect
  // (React's documented "adjusting state when a prop changes"). An effect
  // would paint one frame of the previous account's choice before correcting
  // itself — on a per-user preference that frame is the whole bug — and the
  // repo's lint ratchet counts `set-state-in-effect` for the same reason.
  const [seededFor, setSeededFor] = useState<string | null>(userId);
  if (seededFor !== userId) {
    setSeededFor(userId);
    setLocalBoard(readCfbLandingBoard(userId));
  }

  // Cross-tab sync, scoped to the signed-in user's key. The `storage` event
  // fires only in OTHER tabs, so re-reading here cannot loop; the key check
  // keeps every unrelated write in the app from re-rendering this panel.
  useEffect(() => {
    if (!userId) return;
    const key = cfbLandingStorageKey(userId);
    const onStorage = (e: StorageEvent) => {
      // A null key is a whole-store `localStorage.clear()`, which wipes this
      // preference too — so it must re-read rather than be filtered out with
      // the unrelated writes the key check exists to ignore.
      if (e.key === null || e.key === key) setLocalBoard(readCfbLandingBoard(userId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);

  const setBoard = useCallback(
    (next: CfbLandingBoard) => {
      writeCfbLandingBoard(userId, next);
      // Set from the value we asked storage to hold rather than re-reading:
      // a failed write (private mode) still leaves the row answering the
      // trader's click for this session, matching the other preference hooks.
      setLocalBoard(next);
    },
    [userId],
  );

  return { board, setBoard };
}
